import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, ShoppingCart, CreditCard, Truck, RotateCcw, Shield, Scale, AlertTriangle, Mail } from 'lucide-react';

const CGVPage = () => {
    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à l'accueil
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            <FileText className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Conditions Générales de Vente</h1>
                            <p className="text-slate-500">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
                        </div>
                    </div>

                    {/* Préambule */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-amber-900 mb-1">Plateforme réservée aux professionnels</p>
                                <p className="text-sm text-amber-700">
                                    Les présentes CGV s'appliquent exclusivement aux relations commerciales entre 
                                    ECO MOBILE SOLUTION et ses clients professionnels (B2B). L'accès à la plateforme 
                                    PHONEB2B nécessite la validation préalable d'un compte professionnel.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Table des matières */}
                    <nav className="bg-slate-50 rounded-xl p-6 mb-8">
                        <h2 className="font-semibold text-slate-900 mb-3">Sommaire</h2>
                        <ol className="space-y-2 text-sm text-blue-600 columns-2">
                            <li><a href="#article-1" className="hover:underline">Article 1 - Objet</a></li>
                            <li><a href="#article-2" className="hover:underline">Article 2 - Identification</a></li>
                            <li><a href="#article-3" className="hover:underline">Article 3 - Accès à la plateforme</a></li>
                            <li><a href="#article-4" className="hover:underline">Article 4 - Produits</a></li>
                            <li><a href="#article-5" className="hover:underline">Article 5 - Prix</a></li>
                            <li><a href="#article-6" className="hover:underline">Article 6 - Commandes</a></li>
                            <li><a href="#article-7" className="hover:underline">Article 7 - Paiement</a></li>
                            <li><a href="#article-8" className="hover:underline">Article 8 - Livraison</a></li>
                            <li><a href="#article-9" className="hover:underline">Article 9 - Réception</a></li>
                            <li><a href="#article-10" className="hover:underline">Article 10 - SAV et retours</a></li>
                            <li><a href="#article-11" className="hover:underline">Article 11 - Garanties</a></li>
                            <li><a href="#article-12" className="hover:underline">Article 12 - Responsabilité</a></li>
                            <li><a href="#article-13" className="hover:underline">Article 13 - Données personnelles</a></li>
                            <li><a href="#article-14" className="hover:underline">Article 14 - Propriété intellectuelle</a></li>
                            <li><a href="#article-15" className="hover:underline">Article 15 - Droit applicable</a></li>
                        </ol>
                    </nav>

                    <div className="prose prose-slate max-w-none space-y-8">
                        
                        {/* Article 1 */}
                        <section id="article-1">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <FileText className="w-5 h-5 text-blue-600" />
                                Article 1 - Objet
                            </h2>
                            <p>
                                Les présentes Conditions Générales de Vente (CGV) régissent l'ensemble des relations 
                                commerciales entre la société ECO MOBILE SOLUTION, exploitant la plateforme PHONEB2B, 
                                et tout professionnel (ci-après « le Client ») effectuant un achat sur la plateforme.
                            </p>
                            <p>
                                Toute commande passée sur la plateforme implique l'acceptation sans réserve des présentes CGV. 
                                Le Client reconnaît avoir pris connaissance des CGV avant de passer commande.
                            </p>
                            <p>
                                ECO MOBILE SOLUTION se réserve le droit de modifier les présentes CGV à tout moment. 
                                Les CGV applicables sont celles en vigueur à la date de la commande.
                            </p>
                        </section>

                        {/* Article 2 */}
                        <section id="article-2">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 2 - Identification du vendeur</h2>
                            <div className="bg-slate-50 rounded-xl p-5">
                                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-slate-500">Raison sociale</p>
                                        <p className="font-medium text-slate-900">ECO MOBILE SOLUTION</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Forme juridique</p>
                                        <p className="font-medium text-slate-900">SAS au capital de 1 000 €</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">SIREN</p>
                                        <p className="font-medium text-slate-900">982 341 869</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">N° TVA</p>
                                        <p className="font-medium text-slate-900">FR40 982 341 869</p>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <p className="text-slate-500">Siège social</p>
                                        <p className="font-medium text-slate-900">
                                            ZI N2 CD 70 Le Biplan, Route d'Hérin, 59220 Rouvignies, France
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Email</p>
                                        <p className="font-medium text-blue-600">
                                            <a href="mailto:welcome@phoneb2b.com">welcome@phoneb2b.com</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Article 3 */}
                        <section id="article-3">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 3 - Accès à la plateforme</h2>
                            <p>
                                L'accès à la plateforme PHONEB2B est strictement réservé aux professionnels. 
                                Pour créer un compte, le Client doit :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Être une personne morale ou un entrepreneur individuel</li>
                                <li>Disposer d'un numéro SIREN/SIRET valide</li>
                                <li>Fournir des informations exactes et complètes lors de l'inscription</li>
                                <li>Attendre la validation de son compte par ECO MOBILE SOLUTION</li>
                            </ul>
                            <p className="mt-3">
                                ECO MOBILE SOLUTION se réserve le droit de refuser ou de suspendre tout compte 
                                sans avoir à justifier sa décision. Toute fausse déclaration pourra entraîner 
                                la fermeture immédiate du compte et des poursuites judiciaires.
                            </p>
                        </section>

                        {/* Article 4 */}
                        <section id="article-4">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                                Article 4 - Produits
                            </h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">4.1 Description des produits</h3>
                            <p>
                                ECO MOBILE SOLUTION propose à la vente des smartphones d'occasion, fonctionnels 
                                ou à réparer. Chaque appareil fait l'objet d'une fiche produit détaillée indiquant :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>La marque, le modèle et la capacité de stockage</li>
                                <li>Le grade cosmétique (A, B, C)</li>
                                <li>L'état esthétique précis (châssis, écran, vitre arrière)</li>
                                <li>Le niveau de batterie</li>
                                <li>Les pannes identifiées ou éléments non testables</li>
                                <li>Le statut opérateur (déverrouillé ou non)</li>
                                <li>Les pièces manquantes ou remplacées</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">4.2 Disponibilité</h3>
                            <p>
                                Les produits sont proposés dans la limite des stocks disponibles. Le stock est mis à jour 
                                en temps réel. En cas d'indisponibilité d'un produit après commande, le Client sera informé 
                                dans les plus brefs délais et pourra obtenir le remboursement de sa commande.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">4.3 Réservation panier</h3>
                            <p>
                                Les articles ajoutés au panier sont réservés pendant une durée de 10 minutes. 
                                Passé ce délai, les articles sont remis en stock et peuvent être achetés par d'autres clients.
                            </p>
                        </section>

                        {/* Article 5 */}
                        <section id="article-5">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                                Article 5 - Prix
                            </h2>
                            <p>
                                Les prix affichés sur la plateforme sont exprimés en euros et s'entendent :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li><strong>HT</strong> (Hors Taxes) - La TVA applicable sera ajoutée lors du récapitulatif de commande</li>
                                <li><strong>Hors frais de livraison</strong>, qui sont calculés et affichés avant validation</li>
                            </ul>
                            <p className="mt-3">
                                La TVA applicable est soit la TVA sur marge (article 297 A du CGI pour les biens d'occasion), 
                                soit la TVA classique selon la nature du produit. Le régime de TVA est indiqué sur la facture.
                            </p>
                            <p className="mt-3">
                                ECO MOBILE SOLUTION se réserve le droit de modifier ses prix à tout moment. 
                                Les produits sont facturés au prix en vigueur au moment de la validation de la commande.
                            </p>
                        </section>

                        {/* Article 6 */}
                        <section id="article-6">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 6 - Commandes</h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">6.1 Processus de commande</h3>
                            <p>Le Client passe commande en suivant les étapes suivantes :</p>
                            <ol className="list-decimal list-inside text-slate-700 space-y-1 mt-2">
                                <li>Sélection des produits et ajout au panier</li>
                                <li>Vérification du panier et des quantités</li>
                                <li>Choix du mode de livraison</li>
                                <li>Validation des informations de facturation et livraison</li>
                                <li>Acceptation des CGV</li>
                                <li>Paiement sécurisé</li>
                            </ol>

                            <h3 className="font-semibold mt-4 mb-2">6.2 Confirmation de commande</h3>
                            <p>
                                Après validation du paiement, le Client reçoit un email de confirmation récapitulant 
                                les détails de sa commande. Cet email vaut acceptation de la commande par ECO MOBILE SOLUTION.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">6.3 Modification / Annulation</h3>
                            <p>
                                Toute demande de modification ou d'annulation doit être adressée par email dans les plus 
                                brefs délais. ECO MOBILE SOLUTION fera ses meilleurs efforts pour y donner suite, sous 
                                réserve que la commande n'ait pas déjà été expédiée.
                            </p>
                        </section>

                        {/* Article 7 */}
                        <section id="article-7">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 7 - Paiement</h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">7.1 Moyens de paiement</h3>
                            <p>Le paiement peut être effectué par :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li><strong>Carte bancaire</strong> via notre prestataire sécurisé Stripe</li>
                                <li><strong>Virement bancaire</strong> sur demande (coordonnées communiquées par email)</li>
                                <li><strong>Avoir client</strong> (crédit disponible sur le compte)</li>
                                <li><strong>Paiement mixte</strong> (avoir + carte bancaire)</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">7.2 Sécurité des paiements</h3>
                            <p>
                                Les transactions par carte bancaire sont sécurisées par notre prestataire Stripe, 
                                certifié PCI-DSS. ECO MOBILE SOLUTION n'a jamais accès aux données complètes de 
                                carte bancaire du Client.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">7.3 Facturation</h3>
                            <p>
                                Une facture est automatiquement générée et envoyée par email après chaque commande. 
                                Les factures sont également accessibles depuis l'espace client.
                            </p>
                        </section>

                        {/* Article 8 */}
                        <section id="article-8">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Truck className="w-5 h-5 text-blue-600" />
                                Article 8 - Livraison
                            </h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">8.1 Zones de livraison</h3>
                            <p>
                                ECO MOBILE SOLUTION livre principalement en France métropolitaine. 
                                Les livraisons vers d'autres destinations peuvent être étudiées sur demande.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">8.2 Délais de livraison</h3>
                            <p>
                                Les commandes validées avant 17h00 (jours ouvrés) sont généralement expédiées le jour même. 
                                Les délais de livraison indicatifs sont :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Express 24h : livraison le lendemain ouvré</li>
                                <li>Standard : 2 à 3 jours ouvrés</li>
                                <li>Point relais : 3 à 5 jours ouvrés</li>
                            </ul>
                            <p className="mt-3 text-sm text-slate-600">
                                Ces délais sont donnés à titre indicatif et ne constituent pas un engagement ferme.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">8.3 Frais de livraison</h3>
                            <p>
                                Les frais de livraison sont calculés en fonction du poids, des dimensions et de la 
                                destination. Ils sont affichés avant la validation de la commande.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">8.4 Transfert de risques</h3>
                            <p>
                                Le transfert de propriété et des risques s'effectue à la livraison, c'est-à-dire 
                                au moment de la remise physique des produits au Client ou à son représentant.
                            </p>
                        </section>

                        {/* Article 9 */}
                        <section id="article-9">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 9 - Réception et vérification</h2>
                            <p>
                                Le Client s'engage à vérifier l'état des produits à la réception et à émettre 
                                les réserves nécessaires auprès du transporteur en cas d'anomalie visible 
                                (colis endommagé, ouvert, etc.).
                            </p>
                            <p className="mt-3">
                                <strong>Délai de signalement :</strong> Toute non-conformité ou défaut doit être 
                                signalé à ECO MOBILE SOLUTION dans un délai de <strong>48 heures</strong> suivant 
                                la réception, par email avec photos à l'appui.
                            </p>
                            <p className="mt-3">
                                Passé ce délai, les produits seront réputés conformes et acceptés par le Client.
                            </p>
                        </section>

                        {/* Article 10 */}
                        <section id="article-10">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <RotateCcw className="w-5 h-5 text-blue-600" />
                                Article 10 - Service Après-Vente et Retours
                            </h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">10.1 Droit de rétractation</h3>
                            <p>
                                Conformément à l'article L221-3 du Code de la consommation, le droit de rétractation 
                                ne s'applique pas aux ventes conclues entre professionnels. Le Client professionnel 
                                ne bénéficie donc pas du délai de rétractation de 14 jours.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">10.2 Procédure SAV</h3>
                            <p>
                                En cas de produit non conforme à la description ou présentant un défaut non mentionné, 
                                le Client peut initier une demande SAV depuis son espace client dans un délai de 
                                <strong> 7 jours</strong> suivant la réception.
                            </p>
                            <p className="mt-2">La procédure est la suivante :</p>
                            <ol className="list-decimal list-inside text-slate-700 space-y-1 mt-2">
                                <li>Création d'une demande SAV avec description du problème et photos</li>
                                <li>Examen de la demande par notre équipe sous 24-48h</li>
                                <li>Si acceptée : envoi d'une étiquette de retour prépayée (si demandée)</li>
                                <li>Réception et vérification du produit retourné</li>
                                <li>Émission d'un avoir ou remboursement selon le cas</li>
                            </ol>

                            <h3 className="font-semibold mt-4 mb-2">10.3 Conditions de retour</h3>
                            <p>Les produits retournés doivent être :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Dans leur état d'origine (non modifiés, non réparés par le Client)</li>
                                <li>Accompagnés de tous les accessoires livrés</li>
                                <li>Correctement emballés pour éviter tout dommage pendant le transport</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">10.4 Remboursement</h3>
                            <p>
                                Les remboursements sont effectués sous forme d'avoir sur le compte client ou par 
                                virement bancaire, dans un délai de 48 heures suivant la validation du retour.
                            </p>
                        </section>

                        {/* Article 11 */}
                        <section id="article-11">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Shield className="w-5 h-5 text-blue-600" />
                                Article 11 - Garanties
                            </h2>
                            
                            <h3 className="font-semibold mt-4 mb-2">11.1 Garantie légale de conformité</h3>
                            <p>
                                ECO MOBILE SOLUTION est tenue de la garantie légale de conformité prévue aux 
                                articles L217-4 et suivants du Code de la consommation, applicable aux ventes 
                                de biens d'occasion entre professionnels et consommateurs.
                            </p>

                            <h3 className="font-semibold mt-4 mb-2">11.2 Exclusions de garantie</h3>
                            <p>La garantie ne couvre pas :</p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Les défauts et pannes mentionnés dans la fiche produit</li>
                                <li>Les dommages résultant d'une mauvaise utilisation</li>
                                <li>Les dommages causés par une intervention ou réparation non autorisée</li>
                                <li>L'usure normale du produit</li>
                                <li>Les dommages causés par un choc, une chute ou un contact avec un liquide</li>
                            </ul>

                            <h3 className="font-semibold mt-4 mb-2">11.3 Produits « en l'état » / « à réparer »</h3>
                            <p>
                                Les appareils vendus comme « à réparer » ou avec des pannes identifiées sont vendus 
                                en l'état et <strong>ne sont pas couverts par la garantie</strong>.
                            </p>
                            <p className="mt-2">
                                Les pannes mentionnées sur la fiche produit sont indiquées <strong>à titre indicatif</strong> et 
                                ne pourront faire l'objet d'une demande de garantie, que ce soit avant ou après une tentative de réparation.
                            </p>
                            <p className="mt-2">
                                Un téléphone en panne est par définition destiné à être réparé. Des pannes supplémentaires 
                                peuvent se révéler uniquement après intervention technique et ne sauraient engager la responsabilité 
                                d'ECO MOBILE SOLUTION.
                            </p>
                        </section>

                        {/* Article 12 */}
                        <section id="article-12">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 12 - Responsabilité</h2>
                            <p>
                                ECO MOBILE SOLUTION s'engage à apporter tout le soin nécessaire à la fourniture 
                                de produits conformes à leur description. Sa responsabilité ne saurait être engagée 
                                dans les cas suivants :
                            </p>
                            <ul className="list-disc list-inside text-slate-700 space-y-1 mt-2">
                                <li>Non-respect par le Client des présentes CGV</li>
                                <li>Utilisation anormale ou non conforme des produits</li>
                                <li>Fait d'un tiers ou cas de force majeure</li>
                                <li>Dommages indirects (perte de chiffre d'affaires, de clientèle, etc.)</li>
                            </ul>
                            <p className="mt-3">
                                En tout état de cause, la responsabilité d'ECO MOBILE SOLUTION est limitée au 
                                montant de la commande concernée.
                            </p>
                        </section>

                        {/* Article 13 */}
                        <section id="article-13">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 13 - Données personnelles</h2>
                            <p>
                                Les données personnelles collectées lors de la création de compte et des commandes 
                                sont traitées conformément au Règlement Général sur la Protection des Données (RGPD) 
                                et à notre <Link to="/privacy" className="text-blue-600 hover:underline">Politique de Confidentialité</Link>.
                            </p>
                            <p className="mt-3">
                                Le Client dispose d'un droit d'accès, de rectification, d'effacement et de portabilité 
                                de ses données qu'il peut exercer en contactant ECO MOBILE SOLUTION à l'adresse 
                                <a href="mailto:welcome@phoneb2b.com" className="text-blue-600 ml-1">welcome@phoneb2b.com</a>.
                            </p>
                        </section>

                        {/* Article 14 */}
                        <section id="article-14">
                            <h2 className="text-xl font-bold text-slate-900 mb-4">Article 14 - Propriété intellectuelle</h2>
                            <p>
                                L'ensemble des éléments de la plateforme PHONEB2B (textes, images, logos, logiciels, 
                                base de données) est protégé par le droit de la propriété intellectuelle et reste 
                                la propriété exclusive d'ECO MOBILE SOLUTION.
                            </p>
                            <p className="mt-3">
                                Toute reproduction, représentation ou exploitation non autorisée est interdite et 
                                constituerait une contrefaçon sanctionnée par les articles L335-2 et suivants du 
                                Code de la Propriété Intellectuelle.
                            </p>
                        </section>

                        {/* Article 15 */}
                        <section id="article-15">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 mb-4">
                                <Scale className="w-5 h-5 text-blue-600" />
                                Article 15 - Droit applicable et litiges
                            </h2>
                            <p>
                                Les présentes CGV sont soumises au droit français. Tout litige relatif à leur 
                                interprétation ou à leur exécution relève de la compétence exclusive des tribunaux 
                                de Valenciennes, nonobstant pluralité de défendeurs ou appel en garantie.
                            </p>
                            <p className="mt-3">
                                En cas de litige, les parties s'efforceront de trouver une solution amiable avant 
                                toute action judiciaire. Le Client peut adresser ses réclamations à :
                            </p>
                            <div className="bg-slate-50 rounded-xl p-4 mt-3">
                                <p className="font-medium">ECO MOBILE SOLUTION</p>
                                <p className="text-sm text-slate-600">ZI N2 CD 70 Le Biplan, Route d'Hérin</p>
                                <p className="text-sm text-slate-600">59220 Rouvignies, France</p>
                                <p className="text-sm text-blue-600 mt-1">
                                    <a href="mailto:welcome@phoneb2b.com">welcome@phoneb2b.com</a>
                                </p>
                            </div>
                        </section>

                    </div>

                    {/* Links to other legal pages */}
                    <div className="border-t border-slate-200 mt-8 pt-6">
                        <h2 className="text-lg font-semibold text-slate-900 mb-4">Documents complémentaires</h2>
                        <div className="flex flex-wrap gap-4">
                            <Link 
                                to="/privacy" 
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                            >
                                <Shield className="w-4 h-4" />
                                Confidentialité & Cookies
                            </Link>
                            <Link 
                                to="/mentions" 
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                            >
                                <FileText className="w-4 h-4" />
                                Mentions légales
                            </Link>
                        </div>
                    </div>

                    {/* Dernière mise à jour */}
                    <p className="text-center text-sm text-slate-400 mt-8 pt-4 border-t">
                        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>

                    {/* Retour à l'accueil */}
                    <div className="mt-8 text-center">
                        <Link 
                            to="/" 
                            onClick={() => window.scrollTo(0, 0)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CGVPage;
